import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getPatchedFields } from 'state/formData/patchedFields/patchedFields.selectors';
import type { AppState } from 'state/state.model';

import type { PatchedFieldsContextValue } from './PatchedFieldsContext';
import { PatchedFieldsContext } from './PatchedFieldsContext';

type WithPatchedFieldsContextProps = {
  formId: string;
  children: ReactNode;
};

export function WithPatchedFieldsContext({ formId, children }: WithPatchedFieldsContextProps) {
  const patchedFields = useSelector((state: AppState) => getPatchedFields(state, formId));
  const pulseCountsRef = useRef<Map<string, number>>(new Map());
  const [pulseParity, setPulseParity] = useState<Readonly<Record<string, number>>>({});

  useEffect(() => {
    const counts = pulseCountsRef.current;
    const current: Record<string, number> = {};
    const patched = new Set(patchedFields);

    for (const fieldId of patchedFields) {
      const next = (counts.get(fieldId) ?? 0) + 1;
      counts.set(fieldId, next);
      current[fieldId] = next;
    }

    for (const fieldId of counts.keys()) {
      if (!patched.has(fieldId)) {
        counts.delete(fieldId);
      }
    }

    setPulseParity(current);
  }, [patchedFields]);

  const value = useMemo<PatchedFieldsContextValue>(
    () => ({ fields: patchedFields, pulseParity }),
    [patchedFields, pulseParity],
  );

  return <PatchedFieldsContext.Provider value={value}>{children}</PatchedFieldsContext.Provider>;
}

WithPatchedFieldsContext.displayName = 'WithPatchedFieldsContext';
